import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { IconName } from '@amelio/ui-library';

/** Whether a 360 shows each respondent's name with their answers, decided per cycle or per standalone 360. */
export const FeedbackPrivacy = {
    NAMED: 'Named',
    ANONYMOUS: 'Anonymous',
} as const;

export type FeedbackPrivacyValue = (typeof FeedbackPrivacy)[keyof typeof FeedbackPrivacy];

export const isFeedbackPrivacy = (value: unknown): value is FeedbackPrivacyValue =>
    Object.values<unknown>(FeedbackPrivacy).includes(value);

/**
 * Mirrors the server's `Feedback360Anonymity.MinimumResponses`: an anonymous 360 shows nothing
 * below this many completed responses, so it cannot be launched with fewer respondents invited.
 * Read wherever no server response carries the value: the launch steps (mode card, respondent
 * count checks, results visibility), the mode pills by default, a survey preview, and the server
 * refusals whose sentence names the number. A response that carries `minimumResponses` comes first.
 */
export const ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES = 3;

export const isAnonymousFeedback = (feedbackPrivacy?: FeedbackPrivacyValue | null): boolean =>
    feedbackPrivacy === FeedbackPrivacy.ANONYMOUS;

/** The copy of the respondent's privacy screen: narrow enough for `Trans` to type. */
export type FeedbackPrivacyCopyKey = Extract<TranslationKey, `answer.feedbackPrivacy.${string}`>;

/** What a 360's mode lets the person evaluated see when they launched it themselves. */
export const revieweeLaunchedCopy = (
    feedbackPrivacy?: FeedbackPrivacyValue,
): FeedbackPrivacyCopyKey =>
    isAnonymousFeedback(feedbackPrivacy)
        ? 'answer.feedbackPrivacy.anonymous.revieweeLaunched'
        : 'answer.feedbackPrivacy.named.revieweeLaunched';

/** How every screen names the mode, through `FeedbackSettingsPills`. */
export const FEEDBACK_PRIVACY_LABEL_KEYS = {
    [FeedbackPrivacy.ANONYMOUS]: 'feedbackPrivacy.label.Anonymous',
    [FeedbackPrivacy.NAMED]: 'feedbackPrivacy.label.Named',
} as const satisfies Record<FeedbackPrivacyValue, TranslationKey>;

/** How every screen pictures the mode, beside its name or what it means for the respondent. */
export const FEEDBACK_PRIVACY_ICONS = {
    [FeedbackPrivacy.ANONYMOUS]: 'security-eye-slash',
    [FeedbackPrivacy.NAMED]: 'security-eye',
} as const satisfies Record<FeedbackPrivacyValue, IconName>;

/** The two 360 settings, carried alike by a cycle's evaluator selection and by a standalone 360. */
export const feedback360SettingsSchema = z.object({
    feedbackPrivacy: z.enum([FeedbackPrivacy.NAMED, FeedbackPrivacy.ANONYMOUS]),
    commentRepliesEnabled: z.boolean(),
});

interface MeetsAnonymousMinimumParams {
    feedbackPrivacy: FeedbackPrivacyValue;
    respondentCount: number;
}

/** A named 360 has no floor; an anonymous one needs the minimum to ever show its answers. */
export const meetsAnonymousMinimum = ({
    feedbackPrivacy,
    respondentCount,
}: MeetsAnonymousMinimumParams): boolean =>
    !isAnonymousFeedback(feedbackPrivacy) ||
    respondentCount >= ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES;

/** How a wizard refuses an anonymous 360 under the minimum; the text says the minimum through `count`. */
export const ANONYMOUS_MINIMUM_REFUSAL = {
    messageKey: 'feedbackPrivacy.anonymousMinimumRespondents',
    values: { count: ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES },
} as const satisfies { messageKey: TranslationKey; values: { count: number } };
